import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  useTheme,
  Button,
  Divider,
} from '@mui/material';
import { Refresh as RefreshIcon, Flag as FlagIcon } from '@mui/icons-material';
import { reportService, Report } from '@/services/admin/reportService';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { FeedItem } from '@/components/dashboard/feed/FeedItem';

const ReportsPage: React.FC = () => {
  const theme = useTheme();
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedContent, setExpandedContent] = useState<Set<string>>(new Set());
  const [openCommentsFeedId, setOpenCommentsFeedId] = useState<string | null>(null);

  const fetchReports = async (pageNum: number, append = false) => {
    setIsLoading(true);
    try {
      const response = await reportService.getReports({
        page: pageNum,
        limit: 10,
      });
      if (response.success) {
        if (append) {
          setReports(prev => [...prev, ...response.data.reports]);
        } else {
          setReports(response.data.reports);
        }
        setHasMore(response.data.pagination.hasNext);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch reports');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
  }, []);

  const handleRefresh = () => {
    setPage(1);
    fetchReports(1);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReports(nextPage, true);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Reported Content Review
        </Typography>
        <IconButton onClick={handleRefresh} disabled={isLoading} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {reports.length === 0 && !isLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">No reports to review at this time.</Typography>
        </Paper>
      ) : (
        <Box>
          {reports.map((report) => (
            <Box key={report._id} sx={{ mb: 4 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  mb: 1, 
                  bgcolor: theme.palette.mode === 'light' ? '#fff4f4' : '#2d1a1a',
                  border: `1px solid ${theme.palette.error.light}`,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <FlagIcon color="error" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="error">
                    Reported by {report.reportedBy?.name || 'Unknown'} ({report.reportedBy?.email})
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Reason: <strong>{report.reason}</strong> • {moment(report.createdAt).fromNow()}
                  </Typography>
                </Box>
              </Paper>
              
              {report.feedId ? (
                <FeedItem
                  feed={report.feedId as any}
                  expandedContent={expandedContent}
                  setExpandedContent={setExpandedContent}
                  onImageClick={() => {}}
                  onDocumentClick={() => {}}
                  openCommentsFeedId={openCommentsFeedId}
                  setOpenCommentsFeedId={setOpenCommentsFeedId}
                  hideActions={true}
                />
              ) : (
                <Paper sx={{ p: 2, textAlign: 'center', border: `1px dashed ${theme.palette.divider}` }}>
                  <Typography variant="body2" color="text.secondary">Reported feed has been deleted or is unavailable.</Typography>
                </Paper>
              )}
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
          
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handleLoadMore} 
                disabled={isLoading}
                sx={{ borderRadius: '20px', px: 4 }}
              >
                {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Load More Reports'}
              </Button>
            </Box>
          )}

          {!hasMore && reports.length > 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
              You've reviewed all reports.
            </Typography>
          )}
        </Box>
      )}

      {isLoading && reports.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ReportsPage;
